import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import BrandLogo from "@/components/feature/BrandLogo";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { resetPassword } from "@/services/passwordReset";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";

export default function ResetPassword() {
  const { settings } = useSiteSettings();
  const [params] = useSearchParams();
  const token = (params.get("token") || "").trim();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not reset your password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title="Reset Password" description="Choose a new password for your account." path="/reset-password" />
      <div className="flex min-h-screen items-center justify-center bg-background-50 px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-8 flex items-center" aria-label={settings.siteName}>
            <BrandLogo className="h-12" />
          </Link>

          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">Choose a new password</h1>

          {!token ? (
            <div className="mt-7 rounded-lg border border-secondary-200 bg-secondary-50 p-5 text-sm text-secondary-900">
              <p className="flex items-center gap-2 font-semibold">
                <i className="ri-error-warning-line text-lg text-secondary-600"></i> Missing reset link
              </p>
              <p className="mt-2 leading-relaxed">
                This page needs a valid reset link. Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 cursor-pointer"
              >
                <i className="ri-mail-send-line"></i> Request a new link
              </Link>
            </div>
          ) : done ? (
            <div className="mt-7 rounded-lg border border-accent-200 bg-accent-50 p-5 text-sm text-accent-900">
              <p className="flex items-center gap-2 font-semibold">
                <i className="ri-checkbox-circle-line text-lg text-accent-600"></i> Password updated
              </p>
              <p className="mt-2 leading-relaxed">
                Your password has been changed. You can now sign in with your new password.
              </p>
              <Link
                to="/login"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 cursor-pointer"
              >
                <i className="ri-login-box-line"></i> Go to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <label
                  htmlFor="password"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500"
                >
                  New password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={show ? "text" : "password"}
                    required
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className={`${inputClass} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 cursor-pointer"
                    aria-label={show ? "Hide password" : "Show password"}
                  >
                    <i className={show ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
              </div>
              <div>
                <label
                  htmlFor="confirm"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500"
                >
                  Confirm password
                </label>
                <input
                  id="confirm"
                  name="confirm"
                  type={show ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter your password"
                  className={inputClass}
                />
              </div>

              {error && (
                <div className="flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-3.5 text-sm text-secondary-900">
                  <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {submitting ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-lock-password-line"></i>
                )}
                {submitting ? "Saving…" : "Set new password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}